import { useState } from "react";
import { motion } from "framer-motion";
import { Download, Copy, Check, ExternalLink } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import EmailTemplateSection from "../components/brand/EmailTemplateSection";
import LetterheadSection from "../components/brand/LetterheadSection";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
  viewport: { once: true },
};

const section = "mb-28";
const sectionTitle = "text-2xl font-semibold mb-10 text-muted-foreground";

const colors = [
  { name: "Primary Green", hex: "#1f3d32", hsl: "hsl(158, 33%, 18%)", css: "bg-primary", usage: "Brand colour, headings, buttons, text" },
  { name: "Cream", hex: "#FDFBF7", hsl: "hsl(60, 30%, 98%)", css: "bg-background", usage: "Page backgrounds" },
  { name: "Lavender", hex: "—", hsl: "hsl(265, 40%, 80%)", css: "bg-secondary", usage: "Secondary accents, tags" },
  { name: "Terracotta", hex: "—", hsl: "hsl(13, 70%, 63%)", css: "bg-accent", usage: "Accent highlights, CTAs" },
  { name: "Muted", hex: "—", hsl: "hsl(60, 10%, 90%)", css: "bg-muted", usage: "Subtle backgrounds, dividers" },
  { name: "Muted Foreground", hex: "—", hsl: "hsl(158, 20%, 40%)", css: "bg-muted-foreground", usage: "Secondary text, captions" },
];

const logoFiles = [
  { label: "Favicon", file: "/favicon.png", desc: "Square logo with green background" },
  { label: "Email — light mode", file: "/email-logo-lightmode.png", desc: "For light email backgrounds" },
  { label: "Email — dark mode", file: "/email-logo-darkmode.png", desc: "For dark email backgrounds" },
];

const Brand = () => {
  const [copied, setCopied] = useState(false);

  const copyAiPrompt = async () => {
    try {
      const res = await fetch("/brand/design.md");
      const text = await res.text();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      window.open("/brand/design.md", "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <section className="w-full pt-32 pb-16 overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 tablet:px-12 laptop:px-20">

          {/* Header */}
          <motion.div {...fadeIn} className="mb-20">
            <h1 className="font-funnel text-5xl tablet:text-6xl font-bold mb-4">
              Brand Kit
            </h1>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl">
              Everything you need to represent qwip — logo, colours, typography, tone of voice, and downloadable assets.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="/brand/qwip-brand-kit.pdf" download>
                <Button variant="default" className="gap-2">
                  <Download size={16} />
                  Download Brand PDF
                </Button>
              </a>
              <Button variant="outline" className="gap-2" onClick={copyAiPrompt}>
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? "Copied!" : "Copy AI Prompt"}
              </Button>
              <a href="/brand/design.md" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" className="gap-2">
                  <ExternalLink size={16} />
                  View design.md
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Logo */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Logo</h2>
            <div className="grid tablet:grid-cols-2 gap-6 mb-6">
              <div className="bg-background border border-border rounded-2xl p-10 flex items-center justify-center">
                <span className="font-funnel font-bold text-5xl text-foreground">qwip</span>
              </div>
              <div className="bg-primary rounded-2xl p-10 flex items-center justify-center">
                <span className="font-funnel font-bold text-5xl text-primary-foreground">qwip</span>
              </div>
            </div>

            <h3 className="text-lg font-semibold mb-4">Download Logo Files</h3>
            <div className="grid tablet:grid-cols-3 gap-4">
              {logoFiles.map((logo) => (
                <div key={logo.label} className="bg-card border border-border rounded-2xl p-5 flex flex-col items-center gap-3">
                  <img src={logo.file} alt={logo.label} className="h-12 object-contain" />
                  <span className="text-sm font-medium">{logo.label}</span>
                  <span className="text-xs text-muted-foreground text-center">{logo.desc}</span>
                  <a href={logo.file} download className="mt-auto">
                    <Button variant="outline" size="sm" className="gap-1.5">
                      <Download size={14} />
                      Download
                    </Button>
                  </a>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Colours */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Colour Palette</h2>
            <div className="grid tablet:grid-cols-2 laptop:grid-cols-3 gap-6">
              {colors.map((c) => (
                <div key={c.name} className="bg-card border border-border rounded-2xl p-6">
                  <div className={`w-full h-24 rounded-xl mb-4 ${c.css} border border-border`} />
                  <h3 className="text-lg font-semibold mb-1">{c.name}</h3>
                  <p className="text-sm font-mono text-muted-foreground mb-0.5">{c.hex !== "—" ? c.hex : c.hsl}</p>
                  <p className="text-xs font-mono text-muted-foreground mb-1">{c.hsl}</p>
                  <p className="text-sm text-muted-foreground">{c.usage}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Typography */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Typography</h2>
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-2xl p-8">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-funnel font-bold text-xl">Funnel Display</h3>
                  <a href="https://fonts.google.com/specimen/Funnel+Display" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground underline">Google Fonts ↗</a>
                </div>
                <p className="text-sm text-muted-foreground mb-4">Headings & display. Weights 400, 700.</p>
                <p className="font-funnel text-4xl tablet:text-6xl font-bold leading-tight">work your way</p>
              </div>

              <div className="bg-card border border-border rounded-2xl p-8">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-funnel font-bold text-xl">Cormorant Garamond</h3>
                  <a href="https://fonts.google.com/specimen/Cormorant+Garamond" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground underline">Google Fonts ↗</a>
                </div>
                <p className="text-sm text-muted-foreground mb-4">Italic accent only. Weight 400. Used for words like "anywhere", "easier", "focus".</p>
                <p className="font-cormorant italic text-4xl tablet:text-6xl leading-tight">anywhere.</p>
              </div>

              <div className="bg-card border border-border rounded-2xl p-8">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-funnel font-bold text-xl">DM Sans</h3>
                  <a href="https://fonts.google.com/specimen/DM+Sans" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground underline">Google Fonts ↗</a>
                </div>
                <p className="text-sm text-muted-foreground mb-4">Body text & UI. Weights 400, 500, 600, 700.</p>
                <div className="space-y-2">
                  <p className="font-normal text-lg">Regular 400 — Private, soundproof pods in the places you already are.</p>
                  <p className="font-medium text-lg">Medium 500 — Bookable by the minute.</p>
                  <p className="font-semibold text-lg">Semibold 600 — Quiet, on demand.</p>
                  <p className="font-bold text-lg">Bold 700 — Become a Partner</p>
                </div>
              </div>
            </div>

            <div className="mt-10 bg-card border border-border rounded-2xl p-10">
              <p className="text-sm text-muted-foreground mb-6">Type pairing — hero lockup</p>
              <h2 className="font-funnel text-5xl tablet:text-7xl font-bold leading-[0.9] tracking-tight">
                work your way,
                <br />
                <span className="font-cormorant italic font-normal">anywhere.</span>
              </h2>
              <p className="text-muted-foreground text-lg mt-4 max-w-xl">
                Private, soundproof pods in the places you already are. Bookable by the minute. Quiet, on demand.
              </p>
            </div>
          </motion.div>

          {/* Tone of Voice */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Tone of Voice</h2>
            <div className="bg-card border border-border rounded-2xl p-8 space-y-6">
              <div>
                <h3 className="font-funnel font-bold text-lg mb-2">Calm confidence, lowercase-first</h3>
                <p className="text-muted-foreground">We write in lowercase where it feels natural. No shouting, no hype. The product speaks for itself.</p>
              </div>
              <div>
                <h3 className="font-funnel font-bold text-lg mb-2">Short, active, human</h3>
                <p className="text-muted-foreground">Short sentences. Active voice. Conversational but not sloppy. We explain complex things simply.</p>
              </div>
              <div>
                <h3 className="font-funnel font-bold text-lg mb-2">Examples</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex gap-2"><span className="text-foreground">→</span> "work your way, <em className="font-cormorant">anywhere.</em>"</li>
                  <li className="flex gap-2"><span className="text-foreground">→</span> "Private, soundproof pods in the places you already are."</li>
                  <li className="flex gap-2"><span className="text-foreground">→</span> "Quiet, on demand."</li>
                  <li className="flex gap-2"><span className="text-foreground">→</span> "Bookable by the minute."</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Usage Rules */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Usage Rules</h2>
            <div className="bg-card border border-border rounded-2xl p-8">
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex gap-2"><span className="text-foreground">•</span> Logo is always lowercase "qwip" — never "Qwip" or "QWIP".</li>
                <li className="flex gap-2"><span className="text-foreground">•</span> Primary Green is used for text AND buttons — the brand is green-led.</li>
                <li className="flex gap-2"><span className="text-foreground">•</span> Cream background is the default — never use pure white (#fff).</li>
                <li className="flex gap-2"><span className="text-foreground">•</span> Cormorant Garamond is only ever used in italic for accent words.</li>
                <li className="flex gap-2"><span className="text-foreground">•</span> Keep copy short and punchy. If a sentence has a comma, consider splitting it.</li>
              </ul>
            </div>
          </motion.div>

          {/* Sample Email Template */}
          <EmailTemplateSection />
          <LetterheadSection />

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Brand;
